import { useNavigate } from 'react-router-dom'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { AtsNewJobModalPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import Select from 'modules/core/components/Inputs/Select'
import Text from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'

import { useAtsContext } from 'modules/ats/lib/atsContext'
import jobs from 'modules/ats/store/collections/jobs'

import { WorkplaceTypeSelect } from '../JobsPage/WorkplaceTypeSelect'

class NewJobForm extends Form {
  constructor(values: {}) {
    super(values, {
      title: 'string',
      location_id: 'number',
      team_id: 'number',
      company_id: 'number',
      ats_company_id: 'number',
      workplace_type: 'string',
      legal_entity_id: 'number',
    })
  }

  get isValid() {
    const hasJobTitle = !!this.get('title').value
    const hasWorkPlace = !!this.get('location_id').value

    return hasJobTitle && hasWorkPlace
  }
}

const NewJobModalPage = () => {
  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { atsCompanyId } = useAtsContext()
  const { loading, data } = useQuery(AtsNewJobModalPageDocument, {})

  const onClose = () => {
    track(trackingEvents.ats.createJobOpeningCancel)
    navigate(navigation.ats.jobs.fullPath())
  }

  const onSuccess = (jobId: number) => {
    track(trackingEvents.ats.jobSettingsJobCreateModalNext)

    navigate(navigation.ats.jobs.jobsId.settings.fullPath({ jobId }))
  }

  useEffect(() => {
    track(trackingEvents.ats.createJobOpeningStart)
  }, [])

  const legalEntitiesOptions: SelectOption[] =
    data?.companies.legalEntitiesConnection.nodes.map((legalEntity) => ({
      label: `${legalEntity.legalName} - ${legalEntity.currency}`,
      value: legalEntity.id,
    })) || []

  const teamsOptions: SelectOption[] =
    data?.teams.teams
      ?.filter((team) => team.name)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((team) => ({ label: team.name, value: team.id })) || []

  const locationOptions: SelectOption[] =
    data?.locations.locations
      ?.filter((location) => location.name)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((location) => ({ label: location.name, value: location.id })) || []

  const defaultLegalEntity = data?.companies.legalEntitiesConnection.nodes[0]?.id

  const form = useMemo(
    () => new NewJobForm({ legal_entity_id: defaultLegalEntity }),
    [defaultLegalEntity]
  )

  const {
    running: submitting,
    performAction: onSubmit,
    success,
    error,
    value,
  } = useAsyncAction(async (closePortal: ClosePortalFn) => {
    const job = await jobs.create({
      ...form.data(),
      company_id: company.id,
      ats_company_id: atsCompanyId,
    })
    dispatch({ type: EventTypes.JOB_POSTING_CREATED })
    closePortal({
      preventOnCloseCallbackAfterClose: true,
    })
    onSuccess(job.id)
  })

  useEffect(() => {
    if (error || success) {
      track(trackingEvents.ats.createJobOpeningSubmit, {
        is_successful: success,
        error_code: error && value?.toString(),
        ...form.data(),
        ats_company_id: atsCompanyId,
      })
    }
  }, [error, success]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      illustration="document-handshake"
      title={i18n.t('ats.jobs_form.main_title')}
      description={i18n.t('ats.jobs_form.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={submitting || !form.isValid} submit>
          {i18n.t('ats.jobs_form.save')}
        </Button>
      }
    >
      {loading ? (
        <Box padding="s40" width="full" alignItems="center">
          <Spinner />
        </Box>
      ) : (
        <Box gap="s24">
          <Text
            autofocus
            label={i18n.t('ats.jobs_form.title')}
            field={form.get('title')}
            mandatory
          />
          {legalEntitiesOptions.length > 1 && (
            <Select
              label={i18n.t('ats.jobs_form.legal_entity')}
              field={form.get('legal_entity_id')}
              options={legalEntitiesOptions}
            />
          )}
          <Select
            none={i18n.t('ats.jobs_form.team_placeholder')}
            label={i18n.t('ats.jobs_form.team')}
            field={form.get('team_id')}
            options={teamsOptions}
          />
          <WorkplaceTypeSelect field={form.get('workplace_type')} />
          <Select
            none={i18n.t('ats.jobs_form.office_placeholder')}
            label={i18n.t('ats.jobs_form.office')}
            field={form.get('location_id')}
            options={locationOptions}
            showOriginalLabelText
            mandatory
          />
          <Callout type="warning" description={i18n.t('ats.jobs_form.workplace_warning_message')} />
        </Box>
      )}
    </Modal>
  )
}

export default observer(NewJobModalPage)
